import {
  COLLAPSIBLE_HEADER_CONTENT_TOP_PADDING,
  CollapsibleScreenHeader,
} from "@/components/prooftree/CollapsibleScreenHeader"
import { EmptyState } from "@/components/prooftree/EmptyState"
import { GlowField } from "@/components/prooftree/GlowField"
import { PressableScale } from "@/components/prooftree/PressableScale"
import { ProgressRing } from "@/components/prooftree/ProgressRing"
import { Badge, BadgeText } from "@/components/ui/badge"
import { Box } from "@/components/ui/box"
import { HStack } from "@/components/ui/hstack"
import { Progress, ProgressFilledTrack } from "@/components/ui/progress"
import { Text } from "@/components/ui/text"
import { VStack } from "@/components/ui/vstack"
import { productBrand, screenCopy } from "@/constants/Brand"
import { Colors } from "@/constants/Colors"
import { useAuth } from "@/contexts/AuthContext"
import {
  CoachRecommendation,
  ContactGrowthOpportunity,
  domains,
  getCoachRecommendations,
  getContactGrowthOpportunities,
  getSkill,
  recentProofs,
} from "@/data/prooftree"
import {
  getEqPassportSummary,
  getEqProofTreeProgress,
  listEqProofPreviews,
  type EqPassportSummary,
  type EqProofPreview,
  type EqProofTreeProgress,
} from "@/services/prooftreeBackend"
import { buildProofTreeImmersion, type NextFocusItem } from "@/services/prooftreeImmersion"
import { router } from "expo-router"
import {
  ArrowUpRight,
  Award,
  ChevronRight,
  FileVideo,
  Lock,
  Play,
  Sparkles,
  Target,
  TrendingDown,
  TrendingUp,
  UsersRound,
  X,
} from "lucide-react-native"
import { useEffect, useRef, useState } from "react"
import { Animated, Platform, ScrollView, TouchableOpacity, View } from "react-native"
import { SafeAreaView } from "react-native-safe-area-context"

const completedLevels = domains.reduce((sum, domain) => sum + domain.completedLevels, 0)
const totalLevels = domains.reduce((sum, domain) => sum + domain.totalLevels, 0)
const eqScore = 42
const todayRecommendations = getCoachRecommendations("home")
const contactGrowthOpportunities = getContactGrowthOpportunities(3)
const localTreeProgress: EqProofTreeProgress = {
  mode: "local",
  domains,
  message: "Using local ProofTree fallback.",
}

const emptySummary: EqPassportSummary = {
  skillCount: domains.reduce((sum, domain) => sum + domain.skills.length, 0),
  levelCount: totalLevels,
  verifiedLevelCount: 0,
  proofCount: 0,
  verifiedProofCount: 0,
  activeTaskCount: 0,
  progressPercent: 0,
}

export default function HomeDashboardScreen() {
  const auth = useAuth()
  const isSignedIn = Boolean(auth.session)
  const [showRecommendations, setShowRecommendations] = useState(true)
  const [passportSummary, setPassportSummary] = useState<EqPassportSummary>(emptySummary)
  const [proofPreviews, setProofPreviews] = useState<EqProofPreview[]>([])
  const [treeProgress, setTreeProgress] = useState<EqProofTreeProgress>(localTreeProgress)
  const scrollY = useRef(new Animated.Value(0)).current
  const displayName =
    auth.profile?.display_name || auth.user?.email?.split("@")[0] || productBrand.productName
  const effectiveSummary = isSignedIn ? passportSummary : undefined
  const effectiveScore = isSignedIn ? (effectiveSummary?.progressPercent ?? 0) : eqScore
  const effectiveCompletedLevels = isSignedIn
    ? (effectiveSummary?.verifiedLevelCount ?? 0)
    : completedLevels
  const effectiveTotalLevels = isSignedIn
    ? (effectiveSummary?.levelCount ?? totalLevels)
    : totalLevels
  const visibleProofs = isSignedIn ? proofPreviews : recentProofs
  const visibleContactGrowthOpportunities = isSignedIn ? [] : contactGrowthOpportunities
  const visibleDomains = treeProgress.domains
  const immersionState = buildProofTreeImmersion(visibleDomains)
  const nextFocusItems = immersionState.nextFocusItems
  const usesSupabaseProgress = isSignedIn && treeProgress.mode === "supabase"

  useEffect(() => {
    let cancelled = false

    async function loadSignedInDashboard() {
      if (!isSignedIn) {
        setPassportSummary(emptySummary)
        setProofPreviews([])
        setTreeProgress(localTreeProgress)
        return
      }

      const [summary, proofs, proofTreeProgress] = await Promise.all([
        getEqPassportSummary(),
        listEqProofPreviews(),
        getEqProofTreeProgress(),
      ])
      if (cancelled) return

      setPassportSummary(summary)
      setProofPreviews(proofs)
      setTreeProgress(proofTreeProgress)
    }

    loadSignedInDashboard()

    return () => {
      cancelled = true
    }
  }, [auth.session?.access_token, isSignedIn])

  return (
    <SafeAreaView
      className="flex-1"
      edges={["top"]}
      style={{ backgroundColor: Colors.dark.surface }}
    >
      <GlowField />
      <CollapsibleScreenHeader
        scrollY={scrollY}
        eyebrow={screenCopy.home.title}
        title={displayName}
        subtitle={screenCopy.home.subtitle}
        statusText={`${effectiveScore}/100 EQ`}
      />
      <Animated.ScrollView
        className="flex-1"
        contentContainerStyle={{
          padding: 24,
          paddingTop: COLLAPSIBLE_HEADER_CONTENT_TOP_PADDING,
          paddingBottom: Platform.OS === "web" ? 132 : 116,
        }}
        showsVerticalScrollIndicator={false}
        scrollEventThrottle={16}
        onScroll={Animated.event([{ nativeEvent: { contentOffset: { y: scrollY } } }], {
          useNativeDriver: false,
        })}
      >
        <Box
          className="mb-6 overflow-hidden rounded-3xl border px-6 py-7"
          style={{
            backgroundColor: `${Colors.dark.primary}1A`,
            borderColor: `${Colors.dark.primary}52`,
            shadowColor: Colors.dark.primary,
            shadowOpacity: 0.24,
            shadowRadius: 24,
            shadowOffset: { width: 0, height: 14 },
          }}
        >
          <VStack className="items-center gap-6">
            <VStack className="items-center gap-1">
              <HStack className="items-center gap-2">
                <Sparkles color={Colors.dark.primary} size={18} />
                <Text className="text-sm font-bold" style={{ color: Colors.dark.primary }}>
                  EMO.coach SCORE
                </Text>
              </HStack>
              <Text className="text-center text-2xl font-bold text-white">
                Your growth signal for today
              </Text>
            </VStack>
            <ProgressRing
              value={effectiveScore}
              size={214}
              strokeWidth={16}
              label={productBrand.tagline}
              centerText={`${effectiveScore}/100`}
            />
            <Text className="text-center leading-5" style={{ color: Colors.dark.textSecondary }}>
              {effectiveCompletedLevels} of {effectiveTotalLevels} levels verified.
              {isSignedIn ? " Add proof to move the score." : " Next milestone: EQ Level 13."}
            </Text>
          </VStack>
        </Box>

        {showRecommendations ? (
          <RecommendedTodayCard
            recommendations={todayRecommendations}
            usesSignedInData={usesSupabaseProgress}
            onDismiss={() => setShowRecommendations(false)}
          />
        ) : null}

        <ContactGrowthOpportunities opportunities={visibleContactGrowthOpportunities} />

        <CurrentFocusCards items={nextFocusItems} usesSignedInData={usesSupabaseProgress} />

        <HStack className="mb-4 items-center justify-between">
          <Text className="text-xl font-bold text-white">EQ Domains</Text>
          <TouchableOpacity onPress={() => router.push("/tree")}>
            <HStack className="items-center gap-1">
              <Text className="font-bold" style={{ color: Colors.dark.primary }}>
                Skill Tree
              </Text>
              <ChevronRight color={Colors.dark.primary} size={16} />
            </HStack>
          </TouchableOpacity>
        </HStack>

        <View className="mb-8 flex-row flex-wrap justify-between gap-y-3">
          {visibleDomains.map((domain) => {
            const Icon = domain.icon
            return (
              <PressableScale
                key={domain.id}
                style={{ width: "48%" }}
                onPress={() => router.push("/tree")}
              >
                <Box
                  className="h-52 rounded-2xl border p-4"
                  style={{
                    backgroundColor: Colors.dark.surfaceSecondary,
                    borderColor: `${domain.color}55`,
                    shadowColor: domain.color,
                    shadowOpacity: 0.11,
                    shadowRadius: 12,
                    shadowOffset: { width: 0, height: 8 },
                  }}
                >
                  <VStack className="h-full justify-between gap-3">
                    <Box
                      className="h-12 w-12 items-center justify-center rounded-2xl"
                      style={{
                        backgroundColor: `${domain.color}22`,
                        shadowColor: domain.color,
                        shadowOpacity: 0.25,
                        shadowRadius: 10,
                      }}
                    >
                      <Icon color={domain.color} size={24} />
                    </Box>
                    <VStack className="gap-3">
                      <VStack className="min-h-16">
                        <Text className="font-bold leading-5 text-white" numberOfLines={2}>
                          {domain.title}
                        </Text>
                        <Text className="mt-1 text-xs" style={{ color: Colors.dark.textSecondary }}>
                          {domain.completedLevels} of {domain.totalLevels} levels completed
                        </Text>
                      </VStack>
                      <Progress size="sm" value={domain.progress} className="bg-background-900">
                        <ProgressFilledTrack style={{ backgroundColor: domain.color }} />
                      </Progress>
                      <Badge
                        className="self-start rounded-full"
                        style={{ backgroundColor: `${domain.color}22` }}
                      >
                        <BadgeText className="font-bold" style={{ color: domain.color }}>
                          {domain.progress}%
                        </BadgeText>
                      </Badge>
                    </VStack>
                  </VStack>
                </Box>
              </PressableScale>
            )
          })}
        </View>

        <Box
          className="mb-8 rounded-3xl border p-5"
          style={{
            backgroundColor: `${Colors.dark.primary}1A`,
            borderColor: `${Colors.dark.primary}4D`,
          }}
        >
          <HStack className="items-start gap-3">
            <Box
              className="h-10 w-10 items-center justify-center rounded-2xl"
              style={{ backgroundColor: `${Colors.dark.primary}29` }}
            >
              <Sparkles color={Colors.dark.primary} size={18} />
            </Box>
            <VStack className="flex-1 gap-1">
              <Text className="text-xs font-bold" style={{ color: Colors.dark.primary }}>
                DAILY COACHING TIP
              </Text>
              <Text className="text-sm leading-5" style={{ color: Colors.dark.textPrimary }}>
                {screenCopy.home.coachTip}
              </Text>
            </VStack>
          </HStack>
        </Box>

        <HStack className="mb-3 items-center gap-2">
          <Award color={Colors.dark.proof} size={19} />
          <Text className="text-xl font-bold text-white">Recent Proofs</Text>
        </HStack>
        {visibleProofs.length > 0 ? (
          <ScrollView horizontal showsHorizontalScrollIndicator={false} className="-mx-5 px-5">
            <HStack className="gap-3">
              {visibleProofs.map((proof) => (
                <PressableScale key={proof.id} onPress={() => router.push("/proof/new")}>
                  <Box
                    className="w-48 rounded-2xl border p-4"
                    style={{
                      backgroundColor: Colors.dark.surfaceSecondary,
                      borderColor: `${proof.color}44`,
                    }}
                  >
                    <Box
                      className="mb-4 h-24 items-center justify-center rounded-xl"
                      style={{ backgroundColor: `${proof.color}20` }}
                    >
                      <Play color={proof.color} size={30} fill={proof.color} />
                    </Box>
                    <Text className="font-bold text-white">{proof.title}</Text>
                    <Text className="mt-1 text-xs" style={{ color: Colors.dark.textSecondary }}>
                      {proof.skill} · {proof.level}
                    </Text>
                    <Text
                      className="mt-3 text-xs font-bold uppercase"
                      style={{ color: proof.color }}
                    >
                      {proof.status}
                    </Text>
                  </Box>
                </PressableScale>
              ))}
            </HStack>
          </ScrollView>
        ) : (
          <View>
            <EmptyState
              icon={FileVideo}
              title="No proofs yet."
              description={
                isSignedIn
                  ? "Your signed-in Supabase profile has no proof submissions yet."
                  : "This is where real growth begins. Add your first proof."
              }
            />
          </View>
        )}
      </Animated.ScrollView>
    </SafeAreaView>
  )
}

function CurrentFocusCards({
  items,
  usesSignedInData,
}: {
  items: NextFocusItem[]
  usesSignedInData: boolean
}) {
  if (items.length === 0) return null

  return (
    <Box
      className="mb-8 rounded-3xl border p-5"
      style={{
        backgroundColor: `${Colors.dark.primary}12`,
        borderColor: `${Colors.dark.primary}45`,
      }}
    >
      <HStack className="mb-4 items-center justify-between gap-3">
        <VStack className="flex-1 gap-1">
          <Text className="text-xs font-bold" style={{ color: Colors.dark.primary }}>
            YOUR CURRENT FOCUS
          </Text>
          <Text className="text-xl font-bold text-white">Next 7 skills and actions</Text>
          <Text className="text-xs" style={{ color: Colors.dark.textSecondary }}>
            {usesSignedInData ? "Based on Supabase progress" : "Using local starter path"}
          </Text>
        </VStack>
        <Box
          className="h-11 w-11 items-center justify-center rounded-2xl"
          style={{ backgroundColor: `${Colors.dark.primary}24` }}
        >
          <Target color={Colors.dark.primary} size={21} />
        </Box>
      </HStack>

      <VStack className="gap-3">
        {items.map((item) => (
          <TouchableOpacity
            key={item.id}
            activeOpacity={0.86}
            onPress={() => router.push(item.route as never)}
          >
            <Box
              className="rounded-2xl border p-4"
              style={{
                backgroundColor: item.locked
                  ? Colors.dark.surfaceSecondary
                  : Colors.dark.surfaceElevated,
                borderColor: item.locked ? `${item.accentColor}33` : `${item.accentColor}66`,
                opacity: item.locked ? 0.82 : 1,
              }}
            >
              <HStack className="items-start gap-3">
                <Box
                  className="h-10 w-10 items-center justify-center rounded-2xl"
                  style={{ backgroundColor: `${item.accentColor}22` }}
                >
                  {item.locked ? (
                    <Lock color={item.accentColor} size={18} />
                  ) : (
                    <Sparkles color={item.accentColor} size={18} />
                  )}
                </Box>
                <VStack className="flex-1 gap-1">
                  <Text className="font-bold text-white">{item.title}</Text>
                  <Text className="text-sm leading-5" style={{ color: Colors.dark.textSecondary }}>
                    {item.reason}
                  </Text>
                  <Text className="text-xs font-bold" style={{ color: item.accentColor }}>
                    {item.actionLabel}
                  </Text>
                </VStack>
                <ArrowUpRight color={item.accentColor} size={19} />
              </HStack>
            </Box>
          </TouchableOpacity>
        ))}
      </VStack>
    </Box>
  )
}

function ContactGrowthOpportunities({
  opportunities,
}: {
  opportunities: ContactGrowthOpportunity[]
}) {
  if (opportunities.length === 0) return null

  return (
    <Box
      className="mb-6 rounded-3xl border p-5"
      style={{
        backgroundColor: `${Colors.dark.growth}14`,
        borderColor: `${Colors.dark.growth}45`,
      }}
    >
      <HStack className="mb-4 items-center justify-between gap-3">
        <HStack className="flex-1 items-center gap-3">
          <Box
            className="h-10 w-10 items-center justify-center rounded-2xl"
            style={{ backgroundColor: `${Colors.dark.growth}24` }}
          >
            <UsersRound color={Colors.dark.growth} size={19} />
          </Box>
          <VStack className="flex-1 gap-1">
            <Text className="text-xs font-bold" style={{ color: Colors.dark.growth }}>
              GROWTH OPPORTUNITIES WITH PEOPLE
            </Text>
            <Text className="text-lg font-bold text-white">Relationships shaping your skills</Text>
          </VStack>
        </HStack>
      </HStack>

      <VStack className="gap-3">
        {opportunities.map((opportunity) => {
          const trend = opportunity.primaryTrend
          const skill = getSkill(trend.skillId)
          const trendColor =
            trend.direction === "negative"
              ? Colors.dark.error
              : trend.direction === "positive"
                ? Colors.dark.growth
                : Colors.dark.textSecondary
          const TrendIcon = trend.direction === "negative" ? TrendingDown : TrendingUp

          return (
            <TouchableOpacity
              key={`${opportunity.contact.id}-${trend.skillId}`}
              activeOpacity={0.86}
              onPress={() =>
                router.push({
                  pathname: "/contact/[id]",
                  params: { id: opportunity.contact.id },
                })
              }
            >
              <Box
                className="rounded-2xl border p-4"
                style={{
                  backgroundColor: Colors.dark.surfaceSecondary,
                  borderColor: `${trendColor}3D`,
                }}
              >
                <HStack className="items-start gap-3">
                  <Box
                    className="h-10 w-10 items-center justify-center rounded-2xl"
                    style={{ backgroundColor: `${trendColor}1F` }}
                  >
                    <TrendIcon color={trendColor} size={18} />
                  </Box>
                  <VStack className="flex-1 gap-1">
                    <HStack className="items-center justify-between gap-2">
                      <Text className="flex-1 font-bold text-white">
                        {opportunity.contact.name}
                      </Text>
                      <Text className="text-sm font-bold" style={{ color: trendColor }}>
                        {trend.changePercent > 0 ? "+" : ""}
                        {trend.changePercent}%
                      </Text>
                    </HStack>
                    <Text className="text-sm" style={{ color: Colors.dark.textSecondary }}>
                      {skill.title} · {opportunity.recommendation}
                    </Text>
                  </VStack>
                  <ChevronRight color={trendColor} size={18} />
                </HStack>
              </Box>
            </TouchableOpacity>
          )
        })}
      </VStack>
    </Box>
  )
}

function RecommendedTodayCard({
  recommendations,
  usesSignedInData,
  onDismiss,
}: {
  recommendations: CoachRecommendation[]
  usesSignedInData: boolean
  onDismiss: () => void
}) {
  return (
    <Box
      className="mb-6 overflow-hidden rounded-3xl border p-5"
      style={{
        backgroundColor: `${Colors.dark.primary}1C`,
        borderColor: `${Colors.dark.primary}57`,
        shadowColor: Colors.dark.primary,
        shadowOpacity: 0.18,
        shadowRadius: 22,
        shadowOffset: { width: 0, height: 12 },
      }}
    >
      <View
        className="absolute -right-8 -top-12 h-32 w-32 rounded-full"
        style={{ backgroundColor: `${Colors.dark.primary}1F` }}
      />
      <HStack className="mb-4 items-start justify-between gap-3">
        <HStack className="flex-1 items-start gap-3">
          <Box
            className="h-12 w-12 items-center justify-center rounded-2xl"
            style={{ backgroundColor: `${Colors.dark.primary}29` }}
          >
            <Target color={Colors.dark.primary} size={22} />
          </Box>
          <VStack className="flex-1 gap-1">
            <Text className="text-xs font-bold" style={{ color: Colors.dark.primary }}>
              RECOMMENDED FOR YOU TODAY
            </Text>
            <Text className="text-xl font-bold text-white">Two focused next actions</Text>
            <Text className="text-sm leading-5" style={{ color: Colors.dark.textSecondary }}>
              {usesSignedInData
                ? "Coach guidance based on your signed-in Supabase progress and open levels."
                : "Coach guidance based on current tasks, proofs, open levels, and available fallback data."}
            </Text>
          </VStack>
        </HStack>
        <TouchableOpacity
          className="h-9 w-9 items-center justify-center rounded-full"
          style={{ backgroundColor: "rgba(255,255,255,0.08)" }}
          onPress={onDismiss}
        >
          <X color={Colors.dark.textSecondary} size={17} />
        </TouchableOpacity>
      </HStack>

      <VStack className="gap-3">
        {recommendations.map((recommendation, index) => (
          <TouchableOpacity
            key={recommendation.id}
            activeOpacity={0.86}
            onPress={() => router.push(recommendation.route as never)}
          >
            <Box
              className="rounded-2xl border p-4"
              style={{
                backgroundColor: index === 0 ? Colors.dark.primary : Colors.dark.surfaceSecondary,
                borderColor:
                  index === 0 ? "rgba(255,255,255,0.2)" : `${recommendation.accentColor}55`,
                shadowColor: index === 0 ? Colors.dark.primary : recommendation.accentColor,
                shadowOpacity: index === 0 ? 0.22 : 0,
                shadowRadius: index === 0 ? 14 : 0,
                shadowOffset: { width: 0, height: 8 },
              }}
            >
              <HStack className="items-start gap-3">
                <Box
                  className="mt-0.5 h-8 w-8 items-center justify-center rounded-full"
                  style={{
                    backgroundColor:
                      index === 0 ? "rgba(3, 33, 29, 0.12)" : `${recommendation.accentColor}22`,
                  }}
                >
                  <Sparkles
                    color={index === 0 ? Colors.dark.primaryForeground : recommendation.accentColor}
                    size={15}
                  />
                </Box>
                <VStack className="flex-1 gap-1">
                  <Text
                    className="font-bold"
                    style={{ color: index === 0 ? Colors.dark.primaryForeground : "white" }}
                  >
                    {recommendation.title}
                  </Text>
                  <Text
                    className="text-sm leading-5"
                    style={{
                      color: index === 0 ? "rgba(3, 33, 29, 0.78)" : Colors.dark.textSecondary,
                    }}
                  >
                    {recommendation.reason}
                  </Text>
                  <Box
                    className="mt-2 self-start rounded-full border px-3 py-1.5"
                    style={{
                      backgroundColor: index === 0 ? "rgba(255,255,255,0.18)" : "transparent",
                      borderColor:
                        index === 0 ? "rgba(255,255,255,0.28)" : `${recommendation.accentColor}66`,
                    }}
                  >
                    <Text
                      className="text-xs font-bold"
                      style={{
                        color:
                          index === 0 ? Colors.dark.primaryForeground : recommendation.accentColor,
                      }}
                    >
                      {recommendation.actionLabel}
                    </Text>
                  </Box>
                </VStack>
                <ChevronRight
                  color={index === 0 ? Colors.dark.primaryForeground : recommendation.accentColor}
                  size={19}
                />
              </HStack>
            </Box>
          </TouchableOpacity>
        ))}
      </VStack>

      <TouchableOpacity
        activeOpacity={0.86}
        className="mt-4 self-start rounded-full px-4 py-2"
        style={{ backgroundColor: "rgba(255,255,255,0.08)" }}
        onPress={() => router.push("/tasks")}
      >
        <Text className="text-sm font-bold" style={{ color: Colors.dark.primary }}>
          See more suggestions
        </Text>
      </TouchableOpacity>
    </Box>
  )
}
